// libs
import React, { memo, useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// components
import PincodeInput from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeInput';

//styles
import { PincodeContainer, DeliveryText, PincodeInputContainer, TrailingButton } from './styles';

// helpers
import { setPincodeToStorage } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';
import {
  getPinCodeDataFromStorage,
  getPinCodeLabelV1,
  setPinCodeToStorage as setPincodeDataToStorage,
} from './helpers';

// hooks
import { useOutsideAlerter } from 'shared/layouts/DetailsPage/components/ProductInfo/Desktop/components/EcomPolicy/hooks';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// constants
import {
  PINCODE_PLACEHOLDER,
  BUTTON_STATE,
  DELIVERY_TEXT,
  DO_NOT_SERVICE_AT_YOUR_LOCATION,
} from './constants';

// analytics
import { onPincodeApply } from './analytics';

// api
import { checkPinCodeService } from 'shared/api/pin-code-service';

// types
import { PincodeApiResponse } from 'shared/layouts/DetailsPage/components/ProductInfo/types';

function PinCodeServiceExp({
  setIsServiceable,
  isMobile = false,
  inStockSKUs,
  fetchDeliveryorShippedDate,
  serviceabiltyResponse,
}: {
  isMobile?: boolean;
  setIsServiceable: (flag: boolean) => void;
  inStockSKUs: string[];
  fetchDeliveryorShippedDate: (
    pinode: string,
    productSKU: string[],
    gatewayApiEnabled: boolean
  ) => void;
  serviceabiltyResponse: PincodeApiResponse;
}) {
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const [pincode, setPincode] = useState('');
  const [previousPincode, setPreviousPincode] = useState('');
  const [helperText, setHelperText] = useState('');
  const [autofocusState, setAutofocusState] = useState(false);
  const [showApply, setShowApply] = useState(false);

  const [apiResponse, setApiResponse] = useState<PincodeApiResponse>(getPinCodeDataFromStorage());
  const [error, setError] = useState(null);
  const gatewayApiEnabled = useRemoteConfig('gatewayApiEnabled');

  useEffect(() => {
    // set apiResponse which is received from parent on component mount
    if (isEmpty(serviceabiltyResponse) === false) {
      setApiResponse(serviceabiltyResponse);
      setPincodeDataToStorage(serviceabiltyResponse);
    }
  }, [pincode, serviceabiltyResponse]);

  useEffect(() => {
    if (pincode.length < 6) {
      setHelperText('');
      setError(null);
    }
    if (pincode !== '' && pincode === previousPincode) {
      const deliveryText = getPinCodeLabelV1(apiResponse);
      setHelperText(deliveryText);
      setError(null);
    }
  }, [apiResponse, pincode, previousPincode]);

  useEffect(() => {
    if (isEmpty(apiResponse) === false) {
      const deliveryText = getPinCodeLabelV1(apiResponse);
      setHelperText(deliveryText);
      setIsServiceable(true);
      setPreviousPincode(apiResponse.pincode);
      setPincode(apiResponse.pincode);
    }
  }, [apiResponse, setIsServiceable]);

  // setting the trailing button value to apply on focus
  useEffect(() => {
    if (inputRef.current === document.activeElement) {
      setShowApply(true);
    }
  }, [setShowApply, inputRef]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length < 6) {
      setIsServiceable(true);
    }
    // Pincode can't have a length greater than 6
    if (e.target.value.length >= 7) {
      return;
    }
    const pincodeValue = e.target.value;
    setPincode(pincodeValue);
  };

  const handleClick = async () => {
    try {
      const data = await checkPinCodeService(Number(pincode));

      // analytics event

      if (data.response.isServicable === 1) {
        const response = await fetchDeliveryorShippedDate(pincode, inStockSKUs, gatewayApiEnabled);
        onPincodeApply({ deliveryDetails: response?.data, previousPincode, pincodeOnPdp: pincode });

        setError(null);
        setApiResponse({ ...data, pincode });

        setPreviousPincode(pincode);
        const deliveryText = getPinCodeLabelV1({ ...data, pincode });
        setHelperText(deliveryText);
        setPincodeDataToStorage({ ...data, pincode });
        setPincodeToStorage(pincode);
        setIsServiceable(true);
        return;
      }

      const message = get(data, 'response.msg', '');
      setHelperText(message);

      if (data.response.isServicable === 0) {
        onPincodeApply({
          previousPincode,
          pincodeOnPdp: pincode,
          pdpCheckDeliveryErrorMessage: message,
        });
      }

      if (message === DO_NOT_SERVICE_AT_YOUR_LOCATION) {
        setIsServiceable(false);
      }
      setError(data);
    } catch (err) {
      // sentry log
    }
  };

  // Note: this is added for Firefox.
  // Firefox allows users to type in characters for input["number"]
  const handleKeyPress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = new RegExp(/^[0-9]*$/);
    return e.key && !e.key.match(regex) && e.preventDefault();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const regex = new RegExp(/^[0-9]*$/);

    // the value that user is trying to paste
    const pasteData = e.clipboardData.getData('text');

    // if pasteData is of type mumber then only allow pasting the text
    if (pasteData && pasteData.match(regex)) {
      setPincode(pasteData.substring(0, 6));
    }
  };

  // on press enter
  const handleFormSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (pincode.length < 6) {
      return;
    }
    handleClick();
    setAutofocusState(false);
    inputRef.current && inputRef.current.blur();
    setShowApply(false);
  };

  const onClear = () => {
    setPincode('');
    setIsServiceable(true);
    setAutofocusState(true);
    inputRef.current && inputRef.current.focus();
  };

  const onEdit = () => {
    setAutofocusState(true);
    inputRef.current && inputRef.current.focus();
    if (previousPincode === pincode) return;
    setIsServiceable(true);
    setShowApply(true);
  };

  const onClickOutsidePinCode = (isError) => {
    setShowApply(false);
    if (!isError) {
      setPincode(previousPincode);
      setIsServiceable(true);
    }
  };

  const isError = error !== null;

  useOutsideAlerter(formRef, () => onClickOutsidePinCode(isError));

  const BUTTON_TEXT =
    pincode.length < 6 || (previousPincode !== pincode && !isError) || showApply
      ? BUTTON_STATE.APPLY
      : isError
      ? BUTTON_STATE.CLEAR
      : BUTTON_STATE.EDIT;

  const trailingButtonClick =
    pincode.length < 6 || (previousPincode !== pincode && !isError) || showApply
      ? handleClick
      : isError
      ? onClear
      : onEdit;

  const hideDeliveryText = pincode !== '' && pincode === previousPincode;

  return (
    <PincodeContainer>
      <PincodeInputContainer>
        {!isMobile && !hideDeliveryText && <DeliveryText>{DELIVERY_TEXT}</DeliveryText>}
        {isMobile && <DeliveryText>{DELIVERY_TEXT}</DeliveryText>}
        <form onSubmit={handleFormSubmit} ref={formRef}>
          <PincodeInput
            value={pincode}
            onChange={(e) => handleChange(e)}
            onKeyPress={(e) => handleKeyPress(e)}
            placeholder={PINCODE_PLACEHOLDER}
            type="number"
            isError={isError}
            helperText={helperText}
            label="Enter Pincode"
            autoFocus={autofocusState}
            onFocus={() => {
              setAutofocusState(true);
            }}
            onPaste={onPaste}
            ref={inputRef}
            trailingComponent={
              <TrailingButton
                type="button"
                tabIndex={0}
                onClick={trailingButtonClick}
                aria-label="close"
                disabled={pincode.length !== 6}
              >
                {BUTTON_TEXT}
              </TrailingButton>
            }
          />
        </form>
      </PincodeInputContainer>
    </PincodeContainer>
  );
}

export default memo(PinCodeServiceExp);
